<template>
    <v-container fluid class="home-page">
        <div class="home-layout">
            <div class="home-fixed-content" :class="{ 'home-fixed-content--condensed': panel !== -1 }">
                <section class="battery-summary" aria-label="Battery status">
                    <div class="battery-card">
                        <h2 class="home-label">Consumption</h2>
                        <div
                            class="battery-reading"
                            role="button"
                            tabindex="0"
                            aria-label="Show main battery history"
                            @click="showGrafDialog1 = true"
                            @keydown.enter.prevent="showGrafDialog1 = true"
                            @keydown.space.prevent="showGrafDialog1 = true"
                        >
                            <Battery
                                :voltage="power1"
                                :loading="statusLoading"
                                :warning="voltageWarning1"
                                :batteryType="getBatteryType(power1)"
                            />
                            <VoltageGraph v-model="showGrafDialog1" id="Battery1" subtitle="Main battery" />
                        </div>
                    </div>

                    <div class="battery-card">
                        <h2 class="home-label">Starter</h2>
                        <div
                            class="battery-reading"
                            role="button"
                            tabindex="0"
                            aria-label="Show starter battery history"
                            @click="showGrafDialog2 = true"
                            @keydown.enter.prevent="showGrafDialog2 = true"
                            @keydown.space.prevent="showGrafDialog2 = true"
                        >
                            <Battery
                                :voltage="power2"
                                :loading="statusLoading"
                                :warning="voltageWarning2"
                                :batteryType="getBatteryType(power2)"
                            />
                            <VoltageGraph v-model="showGrafDialog2" id="Battery2" subtitle="Starter battery" />
                        </div>
                    </div>
                </section>

                <section class="alarm-section" aria-label="Alarm controls">
                    <AlarmButton class="home-alarm" :armed="armed" />
                </section>

                <section class="temperature-section">
                    <button class="temperature-button" type="button" @click="openTemperatureSource">
                        <span class="temperature-value">{{ temperature }}&deg; C</span>
                        <span class="temperature-label">Cabin</span>
                    </button>
                    <SilenceButton v-if="isSilenceSirenAvailable" class="home-silence" />
                </section>
            </div>

            <section
                class="sensor-section"
                :class="{ 'sensor-section--expanded': panel !== -1 }"
                aria-label="Boat sensors"
            >
                <SensorPanelContainer
                    v-model="panel"
                    class="home-sensor-panels"
                    :lastHeard="gpsLocationAge"
                    @lastHeardClick="openLocation"
                >
                    <ShorePowerItem
                        :value="shorePowerModel"
                        :shore-power-meter="shorePowerMeterModel"
                        @enroll="openEnroll"
                        @unpair="unpair"
                    >
                        Shore Power
                    </ShorePowerItem>
                    <SmartPlugItem :value="smartplugModel" @enroll="openEnroll" @unpair="unpair">
                        Smart Plug
                    </SmartPlugItem>
                    <DoorSensorItem :value="doorsensorModel" @enroll="openEnroll" @unpair="unpair">
                        Main Door
                    </DoorSensorItem>
                    <HatchSensorItem :value="hatchsensorModel" @enroll="openEnroll" @unpair="unpair">
                        Deck Hatch Sensor
                    </HatchSensorItem>

                    <template #scroll-content>
                        <v-list id="homeList" class="home-mob-list">
                            <v-list-item-group color="primary">
                                <v-list-item class="mob" @click="goToMob">
                                    <v-list-item-icon>
                                        <v-img
                                            class="sensor-icon"
                                            max-width="40px"
                                            src="@/assets/icons/mobY.svg"
                                        />
                                    </v-list-item-icon>
                                    <v-list-item-content>
                                        <v-list-item-title>MOB - MAN OVER BOARD, PHONE LOCATION</v-list-item-title>
                                    </v-list-item-content>
                                </v-list-item>
                            </v-list-item-group>
                        </v-list>
                    </template>
                </SensorPanelContainer>
            </section>

            <button
                class="position-summary"
                type="button"
                aria-label="Show current location"
                @click="openLocation"
            >
                <span>Latitude: {{ latitude }}&deg;{{ latLetter }}</span>
                <span class="sep" aria-hidden="true">|</span>
                <span>Longitude: {{ longitude }}&deg;{{ lngLetter }}</span>
                <span class="sep" aria-hidden="true">|</span>
                <span>Accuracy: {{ accuracy }}</span>
            </button>
        </div>

        <LocationDialog v-model="showLocationDialog" />
        <TemperatureSourceDialog v-model="showTemperatureSourceDialog" @selected="refreshTemperature" />
    </v-container>
</template>
<script lang="ts">
    import { Component, Vue } from "vue-property-decorator";
    import { alarm, app, sensor } from "@/store";
    import AttachedDeviceModel from "@/models/AttachedDeviceModel";
    import {
        computed,
        defineComponent,
        getCurrentInstance,
        onBeforeUnmount,
        onMounted,
        ref
    } from "vue";
    import { ILocationFromParticle } from "../models/AlarmStatus";
    import useTemperature from "@/store/useTemperature";
    import TemperatureViewModel from "@/models/TemperatureViewModel";
    import { VPagination } from "vuetify/lib/components";
    import { enrollState } from '@/store';
    import { enrollUi } from '@/store/enrollUi';
    import {
        resolveGeolocationAccess,
    } from "@/utilities/useGeolocation";

    export default defineComponent({
        name: "Home",
        components: {
            Battery: () =>
                import(/* webpackChunkName: "components" */ "./components/BatteryView.vue"),
            AlarmButton: () =>
                import(
        /* webpackChunkName: "components" */ "./components/ArmDisarmButton.vue"
                ),
            SilenceButton: () =>
                import(
                    /* webpackChunkName: "components" */ "./components/SilenceButton.vue"
                ),
            SensorItem: () =>
                import(
        /* webpackChunkName: "components" */ "./components/Sensors/DefaultSensorListItem.vue"
                ),
            ShorePowerItem: () =>
                import(
        /* webpackChunkName: "components" */ "./components/Sensors/ShorePowerListItem.vue"
                ),
            SmartPlugItem: () =>
                import(
        /* webpackChunkName: "components" */ "./components/Sensors/SmartPlugListItem.vue"
                ),
            DoorSensorItem: () =>
                import(
        /* webpackChunkName: "components" */ "./components/Sensors/DoorSensorListItem.vue"
                ),
            HatchSensorItem: () =>
                import(
        /* webpackChunkName: "components" */ "./components/Sensors/HatchSensorListItem.vue"
                ),
                SensorPanelContainer: () =>
                import(
            /* webpackChunkName: "components" */ "./components/Sensors/SensorPanelContainer.vue"
                ),
            VoltageGraph: () =>
                import(
        /* webpackChunkName: "components" */ "./components/Dialogs/VoltageDialog.vue"
                ),
            LocationDialog: () =>
                import(
        /* webpackChunkName: "components" */ "./components/Dialogs/LocationDialog.vue"
                ),
            TemperatureSourceDialog: () =>
                import(
        /* webpackChunkName: "components" */ "./components/Dialogs/TemperatureSourceDialog.vue"
                )
        },
        setup() {
            const root = getCurrentInstance()?.proxy as any;
            const showGrafDialog1 = ref<boolean>(false);
            const showGrafDialog2 = ref<boolean>(false);
            const showLocationDialog = ref<boolean>(false);
            const showTemperatureSourceDialog = ref<boolean>(false);
            const connectSensor = ref<AttachedDeviceModel>();
            const gpsLocationAge = ref<string>("");
            let lastGpsTime: Date | null = null;
            let lastHeardIntervalId: any | null = null;
            let stopLastKnownLocationWatch: Function | null = null;
            const { get } = useTemperature();
            const smallVoltageLimit = 0.5;
            const lowVoltageLimit12V = 11.8;
            const highVoltageLimit12V = 14.8;
            const lowVoltageLimit24V = 23.6;
            const highVoltageLimit24V = 29.5;
            const chargingThreshold = 19; // Anything above this might be 24V battery

            const armed = computed(() => { return alarm.armed; });

            function detectBatteryType(voltage: number): "12V" | "24V" {
                return voltage < chargingThreshold ? "12V" : "24V";
            }

            function getBatteryType(voltage: number): string {
                if (voltage === -1) return "Unknown";
                return detectBatteryType(voltage);
            }

            function isVoltageWarning(voltage: number): boolean {
                if (voltage === -1) return false;
                const type = detectBatteryType(voltage);

                return type === "12V"
                    ? voltage < lowVoltageLimit12V || voltage > highVoltageLimit12V
                    : voltage < lowVoltageLimit24V || voltage > highVoltageLimit24V;
            }

            function getVoltage(value: string | undefined): number {
                if (value === "NotPresent") return -1;
                const parsed = parseFloat(value !== undefined && value !== null ? value : "-1");
                return parsed < smallVoltageLimit ? -1 : parsed;
            }

            const power1 = computed(() => {
                return alarm.alarmStatus &&
                    alarm.alarmStatus.VSupplies &&
                    alarm.alarmStatus.VSupplies[0] &&
                    getVoltage(alarm.alarmStatus.VSupplies[0].V1);
            });

            const power2 = computed(() => {
                return alarm.alarmStatus &&
                    alarm.alarmStatus.VSupplies &&
                    alarm.alarmStatus.VSupplies[0] &&
                    getVoltage(alarm.alarmStatus.VSupplies[0].V2);
            });

            const voltageWarning1 = computed(() => isVoltageWarning(power1.value));
            const voltageWarning2 = computed(() => isVoltageWarning(power2.value));

            const hatchsensorModel = computed<AttachedDeviceModel>(() => {
                return getItem(1);
            });
            const shorePowerModel = computed<AttachedDeviceModel | null>(() => {
                if (!Array.isArray(sensor.devices)) {
                    return null;
                }
                const found = sensor.devices.find((device) => device.attachedDeviceTypeId === 2);
                return found ? found : null;
            });
            const shorePowerMeterModel = computed<AttachedDeviceModel | null>(() => {
                if (!Array.isArray(sensor.devices)) {
                    return null;
                }
                const found = sensor.devices.find((device) => device.attachedDeviceTypeId === 14);
                return found ? found : null;
            });
            const smartplugModel = computed<AttachedDeviceModel>(() => {
                return getItem(3);
            });
            const doorsensorModel = computed<AttachedDeviceModel>(() => {
                return getItem(10);
            });
            const temperatureModel = ref<TemperatureViewModel | null>(null);
            const refreshTemperature = () => {
                get().then((model: TemperatureViewModel) => {
                    temperatureModel.value = model;
                });
            };
            refreshTemperature();

            const getItem = (type: number): AttachedDeviceModel => {
                let model: AttachedDeviceModel = new AttachedDeviceModel();
                if (Array.isArray(sensor.devices) && sensor.devices.length > 0) {
                    const foundModel = sensor.devices.find((value: AttachedDeviceModel) => {
                        return value.attachedDeviceTypeId === type;
                    });
                    if (foundModel != null) {
                        return foundModel;
                    }
                }
                model.attachedDeviceTypeId = type;
                return model;
            };

            const doorPaired = computed(() => {
                const model = doorsensorModel.value;
                return model.attachedDeviceId != null && model.attachedDeviceId !== "";
            });

            const hatchPaired = computed(() => {
                const model = hatchsensorModel.value;
                return model.attachedDeviceId != null && model.attachedDeviceId !== "";
            });

            const doorResponsive = computed(() => {
                return doorPaired.value && doorsensorModel.value.age == 0;
            });

            const hatchResponsive = computed(() => {
                return hatchPaired.value && hatchsensorModel.value.age == 0;
            });

            const selectedTemperatureSourceResponsive = computed<boolean | null>(() => {
                const selectedSourceId = temperatureModel.value?.cabinTemperatureSourceId;
                if (!selectedSourceId || !Array.isArray(sensor.devices)) {
                    return null;
                }
                const selectedSource = sensor.devices.find(
                    (device: AttachedDeviceModel) => device.attachedDeviceId === selectedSourceId
                );
                return selectedSource ? selectedSource.age === 0 : false;
            });

            const latitude = computed<string>(() => {
                            return alarm.lat ? alarm.lat.toFixed(5) : "-.--";
            });

            const longitude = computed<string>(() => {
                            return alarm.lng ? alarm.lng.toFixed(5) : "-.--";
            });

            const accuracy = computed<string>(() => {
                const location = alarm.lastKnownLocation && alarm.lastKnownLocation.location;
                const accuracyValue = location && location.horizontal_accuracy;

                if (accuracyValue === undefined || accuracyValue === null) {
                    return "-.--";
                }
                console.log("accuracyValue", accuracyValue.toFixed(0) + " m");
                return accuracyValue.toFixed(0) + " m";
            });

            const latLetter = computed<string>(() => parseFloat(latitude.value) >= 0 ? "N" : "S");

            const lngLetter = computed<string>(() => parseFloat(longitude.value) >= 0 ? "E" : "W");

            const statusLoading = computed<boolean>(() => { return alarm.syncStatus; });

            const goToMob = async () => {
                const accessResolution = await resolveGeolocationAccess();
                console.info("[GeoTrace] Home.goToMob resolved permission state", {
                    permissionState: accessResolution.permissionState,
                    accessStatus: accessResolution.accessStatus,
                });

                if (accessResolution.accessStatus === "denied" || accessResolution.accessStatus === "unavailable") {
                    root.$router.push({ name: "geo-permission", query: { destination: "mob" } });
                    return;
                }

                if (accessResolution.accessStatus === "granted") {
                    root.$router.push({ name: "mob" });
                } else {
                    root.$router.push({ name: "geo-permission", query: { destination: "mob" } });
                }
            };

            const generateTimeString = function (days, hours, minutes, seconds) {
                var timestring = "";

                if (days > 0) timestring += days + "d ";
                if (hours > 0 || timestring !== "") timestring += hours + "h ";
                if (minutes > 0 || timestring !== "") timestring += minutes + "m ";
                timestring += seconds + "s";

                return timestring.trim() + " ago";
            };

            const isSilenceSirenAvailable = computed(() => alarm.isSilenceSirenAvailable);

            function stopLastHeardTimer() {
                if (lastHeardIntervalId) {
                    clearInterval(lastHeardIntervalId);
                    lastHeardIntervalId = null;
                }
            }

            function cleanupPageTimers() {
                stopLastHeardTimer();
                lastGpsTime = null;

                if (stopLastKnownLocationWatch) {
                    stopLastKnownLocationWatch();
                    stopLastKnownLocationWatch = null;
                }
            }

            function initTimer(alarmInstance) {
                if (
                    alarmInstance &&
                    alarmInstance.lastKnownLocation &&
                    alarmInstance.lastKnownLocation.location &&
                    alarmInstance.lastKnownLocation.location.last_heard
                ) {
                    const lastHeardStr = alarmInstance.lastKnownLocation.location.last_heard;
                    const lastHeard = new Date(Date.parse(lastHeardStr)); // should be UTC
                    const lastHeardTime = lastHeard.getTime();

                    if (lastGpsTime && lastGpsTime.getTime() !== lastHeardTime) {
                        stopLastHeardTimer();
                    }

                    if (!lastHeardIntervalId) {
                        lastHeardIntervalId = setInterval(() => {
                            const now = new Date(); // current time
                            const nowUtc = now.getTime(); // in ms (UTC)
                            const distance = nowUtc - lastHeardTime;

                            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
                            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                            //console.log("distance", distance, "nowUtc", new Date(nowUtc).toISOString(), "lastHeard", lastHeard.toISOString());

                            const timestring = generateTimeString(days, hours, minutes, seconds);
                            gpsLocationAge.value = timestring;
                        }, 1000);
                    }

                    lastGpsTime = lastHeard;
                } else {
                    gpsLocationAge.value = "---";
                    stopLastHeardTimer();
                    lastGpsTime = null;
                }
            }

            const panel = computed<number | null>({
                get: () => {
                    if (app.getHomePanelOpen) {
                        console.log("app.getHomePanelIndex", app.getHomePanelIndex);
                        return app.getHomePanelIndex;
                    }
                    console.log("app.getHomePanelIndex", -1);
                    return -1;
                },
                set: (next: number | null) => {
                    const nextIndex = next ?? -1;
                    app.setHomePanelIndex(nextIndex);
                    app.setHomePanelOpen(nextIndex !== -1);
                },
            });

            const temperature = computed(() => {
                const model = temperatureModel.value;
                if (!model || model.cabinTemperature === -273) {
                    return "-.--";
                }
                if (model.cabinTemperatureSourceId) {
                    return selectedTemperatureSourceResponsive.value
                        ? model.cabinTemperature.toFixed(1)
                        : "-.--";
                }
                if (doorResponsive.value || hatchResponsive.value) {
                    return model.cabinTemperature.toFixed(1);
                }
                return "-.--";
            });

            function openTemperatureSource() {
                showTemperatureSourceDialog.value = true;
            }

            function openEnroll(payload: { typeId: number; img: string }) {
                enrollState.startEnrollment(payload.typeId);
                enrollUi.open(payload.img);
                console.log("Home openDialog", payload);
            }

            function unpair(payload: { deviceId: string }) {
                console.log("Home unpair", payload);
                sensor.UnpairDevice(payload.deviceId);
            }

            function openLocation() {
                showLocationDialog.value = true;
            }

            onMounted(() => {
                alarm.getGeoLocation();
                alarm.fetchSecondsSinceLastAlarm();
                stopLastKnownLocationWatch = alarm.$watch(function (m) { return m.lastKnownLocation; }, function () {
                    initTimer(alarm);
                });
                initTimer(alarm);
                sensor.GetData();

                // Print door model and hatch model to console
                console.log("doorsensorModel age", doorsensorModel.value.age);
                console.log("doorsensorModel isConnecting", doorsensorModel.value.isConnecting);
                console.log("hatchsensorModel age", hatchsensorModel.value.age);
                console.log("hatchsensorModel isConnecting", hatchsensorModel.value.isConnecting);
            });

            onBeforeUnmount(() => {
                cleanupPageTimers();
            });

            return {
                armed,
                power1,
                power2,
                shorePowerModel,
                shorePowerMeterModel,
                smartplugModel,
                doorsensorModel,
                hatchsensorModel,
                latitude,
                longitude,
                statusLoading,
                showGrafDialog1,
                showGrafDialog2,
                showLocationDialog,
                showTemperatureSourceDialog,
                openTemperatureSource,
                refreshTemperature,
                openLocation,
                connectSensor,
                gpsLocationAge,
                openEnroll,
                unpair,
                temperature,
                accuracy,
                latLetter,
                lngLetter,
                voltageWarning1,
                voltageWarning2,
                goToMob,
                getBatteryType,
                isSilenceSirenAvailable,
                panel
            };
        }
    });

    @Component({})
    class Home extends Vue {
        public showGrafDialog1: boolean = false;
        public showGrafDialog2: boolean = false;
        public connectSensor: AttachedDeviceModel | null = null;
    }
</script>
<style scoped>
    .theme--light.v-application .home-page {
        position: absolute;
        inset: 0;
        display: flex;
        box-sizing: border-box;
        width: auto;
        height: auto;
        min-height: 0;
        overflow: hidden;
        padding: clamp(32px, 4vh, 40px) max(0px, env(safe-area-inset-right, 0px))
            clamp(4px, 0.7vh, 8px)
            max(0px, env(safe-area-inset-left, 0px));
    }

    .home-layout {
        display: grid;
        width: 100%;
        height: 100%;
        min-height: 0;
        grid-template-rows: minmax(0, 1fr) auto auto;
        gap: clamp(4px, 0.7vh, 8px);
        margin: 0;
    }

    .home-fixed-content {
        display: flex;
        width: 100%;
        max-width: 620px;
        min-height: 0;
        flex-direction: column;
        justify-content: space-evenly;
        justify-self: center;
    }

    .home-fixed-content--condensed {
        justify-content: center;
        gap: clamp(8px, 1vh, 12px);
    }

    .battery-summary {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
    }

    .battery-card {
        min-width: 0;
        text-align: center;
    }

    .home-label {
        margin: 0 0 3px;
        color: rgba(255, 255, 255, 0.7);
        font-size: clamp(var(--app-font-caption), 3vw, var(--app-font-compact));
        font-weight: 400;
        line-height: 1.2;
    }

    .battery-reading {
        display: grid;
        min-height: 26px;
        justify-items: center;
        cursor: pointer;
        outline-offset: 3px;
    }

    .battery-reading ::v-deep .batteryImage {
        display: block;
        max-width: 100%;
        height: auto;
    }

    .alarm-section {
        display: flex;
        justify-content: center;
    }

    .alarm-section ::v-deep .v-row {
        margin: 0;
    }

    .alarm-section ::v-deep .v-col {
        padding: 0;
    }

    .alarm-section ::v-deep .v-image {
        width: clamp(80px, 23vw, 100px) !important;
        max-width: 100px !important;
    }

    .alarm-section ::v-deep .v-col:nth-child(2) {
        font-size: var(--app-font-body);
        line-height: 1.15;
    }

    .alarm-section ::v-deep .v-col:nth-child(2) .caption {
        font-size: var(--app-font-caption) !important;
    }

    .temperature-section {
        text-align: center;
    }

    .temperature-button {
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        padding: 3px 8px;
        border: 0;
        background: transparent;
        color: inherit;
        font: inherit;
        cursor: pointer;
    }

    .temperature-value {
        color: rgba(255, 255, 255, 0.8);
        font-size: clamp(var(--app-font-compact), 3.6vw, var(--app-font-emphasis));
        line-height: 1.2;
    }

    .temperature-label {
        color: rgba(255, 255, 255, 0.7);
        font-size: var(--app-font-caption);
        line-height: 1.2;
    }

    .home-silence {
        margin: 0;
        padding: 2px 0 !important;
    }

    .sensor-section {
        display: flex;
        flex-direction: column;
        gap: 3px;
        width: 100%;
        max-width: 620px;
        height: clamp(190px, 30.5vh, 258px);
        min-width: 0;
        min-height: 0;
        justify-self: center;
        overflow: hidden;
    }

    .sensor-section.sensor-section--expanded {
        height: clamp(220px, 50vh, 440px);
    }

    .home-sensor-panels {
        flex: 1 1 auto;
        min-height: 0;
    }

    .home-sensor-panels ::v-deep .headerRow {
        min-height: 28px;
        font-size: var(--app-font-tiny);
    }

    .home-sensor-panels ::v-deep .v-expansion-panel-header {
        min-height: 42px;
        padding: 1px 8px;
    }

    .home-sensor-panels ::v-deep .sensor-icon {
        width: 40px !important;
        max-width: 40px !important;
    }

    .home-sensor-panels ::v-deep .panel-action--top .panel-icon .v-image {
        width: 32px !important;
        height: 32px !important;
        max-width: 32px !important;
        max-height: 32px !important;
    }

    .home-sensor-panels ::v-deep .sensor-title {
        font-size: var(--app-font-body);
    }

    .home-sensor-panels ::v-deep .v-expansion-panel-content__wrap {
        padding-top: 8px;
        padding-bottom: 8px;
    }

    .home-mob-list {
        flex: 0 0 auto;
        margin: 0;
        padding-bottom: 0;
    }

    #homeList ::v-deep .v-list-item {
        min-height: 44px;
        padding-left: 8px;
    }

    #homeList ::v-deep .v-list-item__content {
        display: flex;
        height: 42px;
        min-height: 42px;
        flex-direction: row;
        align-items: center;
        padding: 0 8px 0 0;
    }

    #homeList ::v-deep .v-list-item__icon {
        display: flex;
        width: 40px;
        min-width: 40px;
        height: 40px;
        flex: 0 0 40px;
        align-items: center;
        justify-content: center;
        margin: 0 8px 0 0;
    }

    #homeList ::v-deep .sensor-icon {
        width: 40px !important;
        height: 40px !important;
        max-width: 40px !important;
        max-height: 40px !important;
    }

    #homeList ::v-deep .v-list-item__title {
        height: auto;
        min-width: 0;
        flex: 1 1 auto;
        padding-left: 0;
        line-height: 1.2;
        font-size: var(--app-font-body) !important;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .position-summary {
        display: flex;
        width: max-content;
        max-width: min(300px, 100%);
        align-items: center;
        justify-self: center;
        justify-content: center;
        gap: 4px;
        min-height: 20px;
        padding: 0;
        color: rgba(255, 255, 255, 0.7);
        font-family: inherit;
        font-size: clamp(0.5rem, 2.2vw, var(--app-font-tiny));
        line-height: 1.2;
        text-align: center;
        cursor: pointer;
    }

    .position-summary > span {
        white-space: nowrap;
    }

    .sep {
        margin-inline: 2px;
        opacity: 0.7;
    }

    .position-summary:focus-visible {
        outline: 2px solid rgba(255, 255, 255, 0.8);
        outline-offset: 2px;
    }

    @media (min-width: 600px) {
        .battery-summary {
            gap: 16px;
        }
    }

    @media (max-height: 720px) and (max-width: 599px) {
        .theme--light.v-application .home-page {
            padding-top: 24px;
            padding-bottom: clamp(4px, 0.7vh, 8px);
        }

        .sensor-section {
            height: clamp(150px, 30vh, 210px);
        }

        .sensor-section.sensor-section--expanded {
            height: clamp(180px, 38vh, 260px);
        }

        .alarm-section ::v-deep .v-image {
            width: clamp(72px, 16vh, 88px) !important;
            max-width: 88px !important;
        }
    }

    @media (min-width: 600px) and (max-height: 850px) {
        .sensor-section.sensor-section--expanded {
            height: clamp(220px, 44vh, 380px);
        }
    }

    @media (min-height: 900px) {
        .sensor-section.sensor-section--expanded {
            height: clamp(220px, 54vh, 560px);
        }
    }

    @media (min-height: 900px) and (max-width: 599px) {
        .sensor-section.sensor-section--expanded {
            height: clamp(220px, 52vh, 540px);
        }
    }

    @supports (-webkit-touch-callout: none) {
        .theme--light.v-application .home-page {
            padding-top: clamp(32px, 4dvh, 40px);
            padding-bottom: clamp(4px, 0.7dvh, 8px);
        }

        .home-layout {
            gap: clamp(4px, 0.7dvh, 8px);
        }

        .sensor-section.sensor-section--expanded {
            height: clamp(180px, 36dvh, 300px);
        }
    }
</style>